import type { SagaIterator } from '@redux-saga/types';
import { batchActions } from 'redux-batched-actions';
import { call, put, select, delay } from 'redux-saga/effects';

import { activeArtboard } from 'containers/Artboards/selectors';
import { documents as documentsSelector } from 'containers/Documents/selectors';
import { layeredLayouts } from 'containers/Layouts/selectors';
import { activeLayer as activeLayerSelector } from 'containers/Project/selectors';
import * as Models from 'models';
import { getFlattenedLayouts } from 'utils/layouts/getFlattenedLayouts';
import { ActionTypes } from '../constants';
import { Action } from '../models';
import {
  relationsByLayoutId,
  modifiedIndicesByRelation,
  mergeLayeredRelations,
  processLayoutBatch,
  getProcessableLayoutIds,
} from './utils';

// Batching mechanism to prevent multiple rapid executions
let batching = false;
export const GLOBAL_RELATIONS_UPDATE_DELAY = 100; // milliseconds

export function* updateLayeredRelationsSilentlyRequest(action: Action.UpdateLayeredRelationsSilently): SagaIterator {
  try {
    const { relations, layoutId } = action.payload;

    // Merge relations if layoutId already exists, otherwise set new ones
    if (relationsByLayoutId[layoutId]) {
      relationsByLayoutId[layoutId] = relationsByLayoutId[layoutId].mergeWith(
        mergeLayeredRelations,
        relations,
      ) as Models.LayeredRelationsMap;
    } else {
      relationsByLayoutId[layoutId] = relations;
    }

    // If there's already a batch scheduled, its already added to the queue - return
    if (batching) {
      return;
    }
    batching = true;

    // Wait for the delay to allow for batching of subsequent calls
    yield delay(GLOBAL_RELATIONS_UPDATE_DELAY);

    const artboard: ReturnTypeSaga<typeof activeArtboard> = yield select(activeArtboard);
    const layouts: ReturnTypeSaga<typeof layeredLayouts> = yield select(layeredLayouts);
    const flattenedLayouts =
      getFlattenedLayouts(artboard, layouts as unknown as Models.CombinedLayoutsMap, true) as unknown as Models.CombinedLayoutsMap | undefined;
    const layoutIdsToBeProcessed = getProcessableLayoutIds(flattenedLayouts, layouts, layoutId);
    const activeLayer: ReturnTypeSaga<typeof activeLayerSelector> = yield select(activeLayerSelector);
    const documents: ReturnTypeSaga<typeof documentsSelector> = yield select(documentsSelector);

    if (!layoutIdsToBeProcessed) {
      return;
    }

    const { actions, relationIdsToCleanup } = yield call(
      processLayoutBatch,
      layoutIdsToBeProcessed,
      layouts,
      activeLayer,
      documents,
    );

    yield put(batchActions(actions, ActionTypes.UPDATE_LAYOUT_ASSETS_SILENTLY));

    // Cleanup modified indices tracking after data is written to store
    (relationIdsToCleanup as string[]).forEach((relationId: string) => {
      delete modifiedIndicesByRelation[relationId];
    });

    layoutIdsToBeProcessed.forEach((id: string) => {
      delete relationsByLayoutId[id];
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    yield call([console, console.error], error);
  } finally {
    batching = false;
  }
}
